/** @file Dopesheet pointer gestures as a pure reducer: scrub, loop range, key click and drag, marquee, pan. */
import { hitTimelineKey } from '@pose2d/editor-geometry/hitTimelineKey';
import { timelineKeysInBox } from '@pose2d/editor-geometry/timelineKeysInBox';
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { ScreenRect, TimelineGroupInput, TimelineLayout, TimelineScale } from '@pose2d/editor-geometry/types';
import type { Id } from '@pose2d/format/types';
import type { KeyRef } from '../../../stores/types';
import type { SelectMode } from '../../../stores/updateSelection';
import { DOPE_PX } from '../draw/dopeMetrics';
import { dopeKeys } from '../model/dopeKeys';
import { keyCommands } from '../model/keyCommands';
import { keySelection } from '../model/keySelection';

/** The gesture in progress between pointer down and up. */
export type DopeGesture =
  | { kind: 'idle' }
  | { kind: 'scrub' }
  | { kind: 'loop'; anchor: number }
  | { kind: 'keys'; x0: number; delta: number; copy: boolean }
  | { kind: 'box'; rect: ScreenRect; mode: SelectMode }
  | { kind: 'pan'; x: number; y: number };

/** A pointer event in canvas CSS px; `mod` is Ctrl (Cmd on macOS). */
export interface DopePointer {
  type: 'down' | 'move' | 'up';
  x: number;
  y: number;
  button: number;
  shift: boolean;
  mod: boolean;
  alt: boolean;
}

/** What the panel does with a gesture step (applyDopeEffects). */
export type DopeEffect =
  | { kind: 'frame'; frame: number }
  | { kind: 'loop'; range: [number, number] }
  | { kind: 'select'; refs: KeyRef[]; mode: SelectMode }
  | { kind: 'drag'; delta: number }
  | { kind: 'move'; delta: number; copy: boolean }
  | { kind: 'box'; rect: ScreenRect | null }
  | { kind: 'pan'; dx: number; dy: number };

/** The dopesheet as the pointer sees it; `selected` is the current SelectionSlice.keys. */
export interface DopeContext {
  layout: TimelineLayout;
  scale: TimelineScale;
  scrollY: number;
  animation: Id;
  groups: ReadonlyMap<string, TimelineGroupInput>;
  selected: readonly KeyRef[];
}

/** The reducer's answer: the next gesture and the effects to apply, in order. */
export interface DopeStep {
  gesture: DopeGesture;
  effects: DopeEffect[];
}

const IDLE: DopeGesture = { kind: 'idle' };
const contentY = (c: DopeContext, y: number): number => y - DOPE_PX.ruler + c.scrollY;
const frameAt = (c: DopeContext, x: number): number => Math.max(0, timelineScale.snapFrame(c.scale, x));
const modeOf = (e: DopePointer): SelectMode => (e.mod ? 'toggle' : e.shift ? 'add' : 'replace');

/** Down on a key: select it unless it is already part of a plain-click selection; a toggle-off starts no drag. */
function downOnKey(e: DopePointer, c: DopeContext, refs: KeyRef[]): DopeStep {
  const mode = modeOf(e);
  const covered = keySelection.covers(c.selected, refs);
  const effects: DopeEffect[] = mode === 'replace' && covered ? [] : [{ kind: 'select', refs, mode }];
  if (mode === 'toggle' && covered) return { gesture: IDLE, effects };
  return { gesture: { kind: 'keys', x0: e.x, delta: 0, copy: e.alt }, effects };
}

function onDown(e: DopePointer, c: DopeContext): DopeStep {
  if (e.button === 1) return { gesture: { kind: 'pan', x: e.x, y: e.y }, effects: [] };
  if (e.button !== 0) return { gesture: IDLE, effects: [] };
  if (e.y < DOPE_PX.ruler) {
    const frame = frameAt(c, e.x);
    return e.shift ? { gesture: { kind: 'loop', anchor: frame }, effects: [] } : { gesture: { kind: 'scrub' }, effects: [{ kind: 'frame', frame }] };
  }
  const hit = hitTimelineKey(c.layout, c.scale, e.x, contentY(c, e.y));
  const row = hit === null ? undefined : c.layout.rows[hit.row];
  const refs = hit === null || row === undefined ? [] : dopeKeys.refsAt(c.animation, c.groups, row, hit.frame);
  if (refs.length > 0) return downOnKey(e, c, refs);
  const rect = { x0: e.x, y0: e.y, x1: e.x, y1: e.y };
  return { gesture: { kind: 'box', rect, mode: modeOf(e) }, effects: [{ kind: 'box', rect }] };
}

type Mover<K extends DopeGesture['kind']> = (g: Extract<DopeGesture, { kind: K }>, e: DopePointer, c: DopeContext) => DopeStep;

const MOVE: { [K in DopeGesture['kind']]: Mover<K> } = {
  idle: (g) => ({ gesture: g, effects: [] }),
  scrub: (g, e, c) => ({ gesture: g, effects: [{ kind: 'frame', frame: frameAt(c, e.x) }] }),
  loop: (g, e, c) => ({ gesture: g, effects: [{ kind: 'loop', range: [g.anchor, frameAt(c, e.x)] }] }),
  keys: (g, e, c) => {
    const delta = keyCommands.clampDelta(c.selected, Math.round((e.x - g.x0) / c.scale.pxPerFrame));
    return { gesture: { ...g, delta }, effects: delta === g.delta ? [] : [{ kind: 'drag', delta }] };
  },
  box: (g, e) => {
    const rect = { ...g.rect, x1: e.x, y1: e.y };
    return { gesture: { ...g, rect }, effects: [{ kind: 'box', rect }] };
  },
  pan: (g, e) => ({ gesture: { kind: 'pan', x: e.x, y: e.y }, effects: [{ kind: 'pan', dx: e.x - g.x, dy: e.y - g.y }] }),
};

/** Keys inside the marquee (canvas px converted to content px), every ref behind each hit. */
function boxRefs(rect: ScreenRect, c: DopeContext): KeyRef[] {
  const content = { x0: rect.x0, x1: rect.x1, y0: contentY(c, rect.y0), y1: contentY(c, rect.y1) };
  return timelineKeysInBox(c.layout, c.scale, content).flatMap((hit) => {
    const row = c.layout.rows[hit.row];
    return row === undefined ? [] : dopeKeys.refsAt(c.animation, c.groups, row, hit.frame);
  });
}

function onUp(g: DopeGesture, c: DopeContext): DopeStep {
  if (g.kind === 'keys') {
    const effects: DopeEffect[] = g.delta === 0 ? [] : [{ kind: 'move', delta: g.delta, copy: g.copy }, { kind: 'drag', delta: 0 }];
    return { gesture: IDLE, effects };
  }
  if (g.kind === 'box') return { gesture: IDLE, effects: [{ kind: 'select', refs: boxRefs(g.rect, c), mode: g.mode }, { kind: 'box', rect: null }] };
  return { gesture: IDLE, effects: [] };
}

/** One pointer event: the next gesture and the effects it causes. Pure; the panel applies the effects. */
export function dopePointer(g: DopeGesture, e: DopePointer, c: DopeContext): DopeStep {
  if (e.type === 'down') return onDown(e, c);
  if (e.type === 'up') return onUp(g, c);
  return (MOVE[g.kind] as Mover<DopeGesture['kind']>)(g, e, c);
}
